import { Button, Modal } from 'antd';
import dayjs from 'dayjs';
import { AlertCircle, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { PATHS } from '@/config/paths';
import { getAnalysisKindMeta } from '@/features/map/utils/analysisKindMeta';
import type { FloodJob } from '@/mocks/floodJobs';

export interface SarJobFailureModalProps {
  open: boolean;
  job: FloodJob | null;
  onClose: () => void;
}

export function SarJobFailureModal({ open, job, onClose }: SarJobFailureModalProps) {
  const { t } = useTranslation();

  if (!job) return null;

  const formatDate = (value?: string) => {
    if (!value) return '';
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : value;
  };

  const kindMeta = getAnalysisKindMeta(job.parameters.analysisKind);
  const errorText = job.errorMessage?.trim() || t('map.sarAoi.jobFailed');
  const { baseline, baselineEnd, crisis, crisisEnd } = job.parameters;
  const baselineLabel = formatDate(baseline);
  const baselineEndLabel = formatDate(baselineEnd);
  const crisisLabel = formatDate(crisis);
  const crisisEndLabel = formatDate(crisisEnd);

  return (
    <Modal
      title={
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-primary)' }}
        >
          <AlertCircle size={18} color="#f87171" />
          <span>{t('map.sarAoi.jobFailedTitle')}</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <Button onClick={onClose}>{t('map.sarAoi.cancel')}</Button>
          <Link to={PATHS.OPS_JOBS}>
            <Button type="primary">{t('ops.jobs.mapProgress.openPipeline')}</Button>
          </Link>
        </div>
      }
      width={560}
      destroyOnHidden
      styles={{
        body: { background: 'var(--mdm-surface)', padding: 16, color: 'var(--text-primary)' },
        header: {
          background: 'transparent',
          borderBottom: '1px solid var(--glass-border)',
        },
      }}
      className="dark-modal"
    >
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 4 }}>{job.name}</div>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            fontSize: 12,
            color: 'var(--text-secondary)',
          }}
        >
          {job.aoi && job.aoi !== '—' && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <MapPin size={12} />
              {job.aoi}
            </span>
          )}
          <span>{t(kindMeta.labelKey)}</span>
          {job.finishedAt && <span>{dayjs(job.finishedAt).format('HH:mm DD/MM/YYYY')}</span>}
        </div>
      </div>

      {(baseline || crisis) && (
        <div
          style={{
            marginBottom: 12,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'var(--map-chrome-panel)',
            border: '1px solid var(--glass-border)',
            fontSize: 12,
            color: 'var(--text-secondary)',
            lineHeight: 1.5,
          }}
        >
          {job.parameters.analysisKind === 'sarFlood' ? (
            <>
              <div>
                {t('map.sarAoi.paramRowReference')}: {baselineLabel}
                {baselineEndLabel && baselineEndLabel !== baselineLabel
                  ? ` → ${baselineEndLabel}`
                  : ''}
              </div>
              <div>
                {t('map.sarAoi.paramRowCompare')}: {crisisLabel}
                {crisisEndLabel && crisisEndLabel !== crisisLabel ? ` → ${crisisEndLabel}` : ''}
              </div>
            </>
          ) : (
            <div>
              {t('map.sarAoi.observationRange')}: {baselineLabel}
              {baselineEndLabel && baselineEndLabel !== baselineLabel
                ? ` → ${baselineEndLabel}`
                : ''}
            </div>
          )}
        </div>
      )}

      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.06em',
          color: 'var(--text-muted)',
          marginBottom: 8,
        }}
      >
        {t('map.sarAoi.jobFailedDetailLabel')}
      </div>
      <div
        role="alert"
        style={{
          padding: '12px 14px',
          borderRadius: 10,
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.35)',
          color: '#fca5a5',
          fontSize: 13,
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
        }}
      >
        {errorText}
      </div>

      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--text-muted)' }}>
        {t('map.sarAoi.jobFailedJobId', { id: job.id })}
      </div>
    </Modal>
  );
}
